/**
 * Logger estruturado para o browser.
 *
 * Emite JSON em uma linha para console (compatível com DataDog, CloudWatch, Loki browser agents).
 * Sanitiza chaves PII antes de qualquer saída — senhas, tokens e dados pessoais nunca são gravados.
 *
 * Níveis: info < warn < error < fatal
 *
 * Uso:
 *   import { logger } from '@/lib/logger'
 *   logger.error('api_error', { status: 500, url: '/funcionarios' })
 *   // ✅ Seguro — não inclua CPF/PIS/senha em campos de contexto
 *
 * NÃO passe PII diretamente: o sanitizador limpa chaves conhecidas, mas não strings livres.
 *   ❌ logger.info(`CPF ${cpf} cadastrado`)   // string livre não é sanitizada
 *   ✅ logger.info('func_created', { func_id: id })
 */

const SENSITIVE_KEYS = new Set([
  'password', 'senha', 'token', 'authorization', 'access_token',
  'refresh_token', 'api_key', 'secret', 'private_key',
  'cpf', 'pis', 'rg', 'passaporte', 'cnh',
  'biometric', 'fingerprint', 'turnstile_token',
])

function sanitize(obj: unknown): unknown {
  if (obj === null || obj === undefined) return obj
  if (Array.isArray(obj)) return obj.map(sanitize)
  if (typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([k, v]) => [
        k,
        SENSITIVE_KEYS.has(k.toLowerCase()) ? '[REDACTED]' : sanitize(v),
      ]),
    )
  }
  return obj
}

type LogLevel = 'info' | 'warn' | 'error' | 'fatal'

// Mínimo de nível a emitir — controlável via variável de ambiente de build.
const MIN_LEVEL_MAP: Record<LogLevel, number> = { info: 0, warn: 1, error: 2, fatal: 3 }
const ENV_LEVEL = (import.meta.env.VITE_LOG_LEVEL ?? 'info').toLowerCase() as LogLevel
const MIN_LEVEL = MIN_LEVEL_MAP[ENV_LEVEL] ?? 0

function emit(level: LogLevel, event: string, context?: Record<string, unknown>): void {
  if (MIN_LEVEL_MAP[level] < MIN_LEVEL) return

  const entry = {
    timestamp: new Date().toISOString(),
    level,
    event,
    ...(context ? (sanitize(context) as object) : {}),
  }

  const output = JSON.stringify(entry)
  if (level === 'fatal' || level === 'error') {
    console.error(output)
  } else if (level === 'warn') {
    console.warn(output)
  } else {
    console.log(output)
  }
}

export const logger = {
  info:  (event: string, ctx?: Record<string, unknown>) => emit('info',  event, ctx),
  warn:  (event: string, ctx?: Record<string, unknown>) => emit('warn',  event, ctx),
  error: (event: string, ctx?: Record<string, unknown>) => emit('error', event, ctx),
  fatal: (event: string, ctx?: Record<string, unknown>) => emit('fatal', event, ctx),
}
